/**
 * Tipos do dominio Windx — fonte unica.
 *
 * Alinhados com os contratos JSON documentados em
 * windx-docs/windx/TEMPLATE_ISSUES_WINDX.md secao 5.
 *
 * Consumidores:
 * - src/services/api/windx.tsx (RTK Query endpoints)
 * - src/helper/windx/downloadCsv.ts (axios download helper)
 * - src/redux/reducers/windxControlPanelReducer.ts (literais de dominio)
 * - Telas: FE-WINDX-005/006/007 (consumidores dos hooks)
 */

// ============ Literais de dominio ============

export type WindxTela = 'sh' | 'sm' | 'comparativo' | 'personalizado';
export type OpcaoRmv = 'com_rmv' | 'sem_rmv';
export type TipoRodada = 'preliminar' | 'final';
export type Patamar = 'media' | 'l' | 'm' | 'p';
export type Frequencia = 'sh' | 'd';
export type Unidade = 'mw' | 'fc';
export type FonteObservado = 'ONS' | 'REN';

// ============ Painel de controle (rota 5.1) ============

export interface Subdivisao {
  id: number;
  nome: string;
  label: string;
}

// WINDX-045 (BE #37 / contrato v5.3): segmentacao de modelos no painel.
// SH usa curto_prazo / climatologia / anos_historicos; SM usa medio_prazo e,
// desde o WEOL-SM com tipo de rodada (#99), climatologia
// (CLIMATOLOGIA_MENSAL_SM e, desde o #101, CLIMATOLOGIA_SM). Modelos sem
// categoria sao anexados ao 1o grupo conhecido (retrocompat pre-deploy BE ou
// modelos fora das 4 categorias do PO).
export type Categoria =
  | 'curto_prazo'
  | 'climatologia'
  | 'anos_historicos'
  | 'medio_prazo';

// Ordem canonica usada pelo agrupador (desktop e mobile) e pelo select de
// categoria do admin de Modelos. Reflete a sequencia visual do painel.
// WEOL-SM (#99): medio_prazo vem logo depois de curto_prazo — no SM o painel
// mostra Medio Prazo antes de Climatologia, como o SH mostra Curto Prazo antes
// de Climatologia. Para o SH nada muda (ele nao tem medio_prazo).
export const ORDEM_CATEGORIAS: readonly Categoria[] = [
  'curto_prazo',
  'medio_prazo',
  'climatologia',
  'anos_historicos',
] as const;

// Mapeamento categoria -> label visivel ao usuario.
export const CATEGORIA_LABEL: Record<Categoria, string> = {
  curto_prazo: 'Curto Prazo',
  climatologia: 'Climatologia',
  anos_historicos: 'Anos Históricos',
  medio_prazo: 'Médio Prazo',
};

export interface Modelo {
  id: number;
  codigo: string;
  label: string;
  // Cor hexadecimal fixa por modelo (v5 do contrato painel-options,
  // 2026-02-23). Usada para legendas e series nos graficos.
  cor: string;
  dias_previsao: number;
  opcoes_rmv: OpcaoRmv[];
  tipo_simulacao?: TipoRodada[];
  // WINDX-045: pode ser null/undefined em retrocompat (BE pre-deploy ou
  // modelos fora das 4 categorias). FE anexa null ao 1o grupo conhecido.
  categoria?: Categoria | null;
}

export interface OpcaoSelect<T extends string> {
  id: T;
  label: string;
}

export interface PainelOptions {
  tela: WindxTela;
  subdivisoes_espaciais: Subdivisao[];
  modelos: Modelo[];
  frequencias_temporais?: OpcaoSelect<Frequencia>[];
  unidades: OpcaoSelect<Unidade>[];
  // `null` quando o painel nao tem a etapa Tipo de Rodada (SM/Comparativo com a
  // flag WINDX_SM_TIPOS_RODADA_ENABLED desligada): o BE serializa o painel com
  // `.dict()` sem `exclude_none`, entao o campo vem `null`, nao omitido.
  tipos_rodada?: OpcaoSelect<TipoRodada>[] | null;
  patamares?: OpcaoSelect<Patamar>[];
}

// ============ Entidades (rota 5.2 — contrato v1.1) ============
//
// Cada entidade tem `disponivel: bool` que reflete EXISTS na tabela fato
// para `data_rodada=hoje` (ou `data_simulacao`) na tela contextual. Quando
// o request omite `tela`, o backend retorna `disponivel=true` em todas
// (backward-compat do contrato v1.0).
//
// Caso concreto: Sudeste tem 0 usinas eolicas no Brasil, entao em SH ele
// vem com `disponivel=false` e o seletor desabilita o checkbox.

export interface EntidadeSimples {
  id: number;
  // WINDX-024 (contrato v2.0):
  // - subdivisao=1/2 (subsistemas, regioes EPE): `nome` + `label`.
  // - subdivisao=3 (conjuntos): `codigo` (cod_conjunto, MUTAVEL — ADR-019) +
  //   `ponto_de_conexao` (nome da subestacao) + `label` (gerado no BD,
  //   `{cod_conjunto}_{UPPER(ponto_de_conexao)}` UNIQUE — usar para exibir
  //   e desambiguar 81/156 casos onde subestacao se repete).
  // Backend usa `exclude_none=True` — campos nao enviados ficam undefined.
  nome?: string;
  codigo?: string;
  ponto_de_conexao?: string;
  label: string;
  disponivel: boolean;
}

export interface Usina {
  id: number;
  codigo: string;
  nome: string;
  potencia: number;
  id_conjunto: number;
  disponivel: boolean;
}

export type Entidade = EntidadeSimples | Usina;

export interface EntidadesListParams {
  subdivisao: number;
  // Quando informado, o backend calcula `disponivel` por entidade para
  // a tela de contexto. Sem `tela` o response e backward-compat (v1.0).
  tela?: WindxTela;
  // YYYY-MM-DD; quando informado vira `data_rodada` no EXISTS (D-14..D-1).
  data_simulacao?: string;
}

// ============ Usinas eólicas denormalizadas (rota 5.12 — WINDX-021) ============
//
// GET /produtos/windx/usinas-eolicas/ — single-fetch das ~1052 usinas com
// 15 campos cada (conjunto + UF + 2 subsistemas + localizacao_epe ja
// resolvidos no SQL JOIN do backend). Frontend consome no Modal
// SeletorEntidades em fluxo hibrido quando subdivisao=4.
//
// Diferente de `Usina` (rota /entidades/list/ subdivisao=4), que vem com 6
// campos e o flag `disponivel`. `UsinaEolicaDenormalizada` cobre o caso
// "tabela completa com filtros e ordenacao client-side" sem fazer N+1.
//
// Contrato canonico: windx-definicao/contratos/common/usinas-eolicas.json v1.0

export interface UsinaEolicaDenormalizada {
  id: number;
  codigo: string;
  nome: string;
  label: string;
  nucleo_ceg: string | null;
  potencia: number;
  lat: number;
  lon: number;
  id_conjunto: number;
  cod_conjunto: string;
  // WINDX-024 (contrato v2.0): `nome_conjunto` substituido por
  // `ponto_de_conexao` (nome da subestacao, semantico) + `label_conjunto`
  // (`{cod_conjunto}_{UPPER(ponto_de_conexao)}` UNIQUE no BD). Use
  // `label_conjunto` para exibir; eh desambiguado quando varios conjuntos
  // compartilham a mesma subestacao (81/156 casos).
  ponto_de_conexao: string;
  label_conjunto: string;
  uf_sigla: string | null;
  uf_nome: string | null;
  subsistema_sm_sigla: string | null;
  subsistema_sh_sigla: string | null;
  localizacao_epe: string | null;
}

export interface UsinasEolicasMeta {
  total: number;
  generated_at: string; // ISO 8601 UTC
  etag_dados: string; // Entre aspas — RFC 7232
}

// O endpoint RTK Query retorna `data` (array) + `meta` (irmaos no envelope).
// O transformResponse extrai a estrutura abaixo para o consumidor.
export interface UsinasEolicasResponse {
  data: UsinaEolicaDenormalizada[];
  meta: UsinasEolicasMeta;
}

// ===== Modal SeletorUsinasEolicas (WINDX-021 Fase 3) =====
//
// Tipos auxiliares usados apenas no modal virtualizado de usinas eolicas
// (subdivisao=4). Mantidos aqui para nao espalhar tipos de dominio em
// `components/`.

export type ColunaOrdenavelUsina =
  | 'codigo'
  | 'nome'
  | 'nucleo_ceg'
  | 'potencia'
  | 'uf_sigla'
  | 'subsistema_sm_sigla'
  | 'subsistema_sh_sigla'
  // WINDX-024: ordenacao por conjunto agora usa label_conjunto
  // (desambiguado), nao nome_conjunto.
  | 'label_conjunto';

export type DirecaoOrdenacao = 'asc' | 'desc';

export interface OrdenacaoUsinas {
  coluna: ColunaOrdenavelUsina;
  direcao: DirecaoOrdenacao;
}

export interface FiltrosUsinas {
  // Busca textual (case-insensitive) — bate em nome, codigo, nucleo_ceg.
  busca: string;
  // Multi-select; vazio = sem filtro.
  ufs: string[];
  subsistemasSm: string[];
  conjuntos: string[];
  // Range de potencia em MW. min/max ausentes = sem limite.
  potenciaMin: number | null;
  potenciaMax: number | null;
}

// ===== Modal SeletorConjuntos (subdivisao=3) =====
//
// Conjunto = agrupador físico de usinas no setor elétrico (cluster pelo
// CEG). Hoje há ~156 conjuntos com pelo menos 1 usina; conjuntos sem
// usina existem em `dim_windx_conjuntos` mas não chegam até aqui porque
// o payload é DERIVADO de `getUsinasEolicas` (rota 5.12) — agregamos no
// FE em vez de bater rota dedicada.
//
// Por que derivar? `useGetUsinasEolicasQuery` já carrega TODOS os campos
// do conjunto denormalizados (cod_conjunto, nome_conjunto, uf_*,
// subsistema_*_sigla, localizacao_epe). Reduce por `id_conjunto` produz
// o payload deste modal sem nova chamada de rede e reaproveita o cache
// (`providesTags: ['WindxUsinasEolicas']`) — abrir o modal de conjuntos
// depois do de usinas é instantâneo.
//
// Limitações conhecidas:
//   - `qtd_usinas` reflete só usinas eólicas (subdivisao=4); no banco
//     pode haver outros tipos de usina vinculados a um conjunto (futuro).
//   - Conjuntos com 0 usinas eólicas ficam invisíveis. Quando o backend
//     expor rota dedicada `/conjuntos/`, trocar a fonte e popular toggle
//     "ocultar conjuntos sem usinas" — hoje seria toggle morto.

export interface ConjuntoAgregado {
  id: number;
  cod_conjunto: string;
  // WINDX-024 (contrato v2.0): `nome_conjunto` substituido por
  // `ponto_de_conexao` (nome da subestacao) + `label` (gerado no BD).
  ponto_de_conexao: string;
  label: string;
  uf_sigla: string | null;
  uf_nome: string | null;
  subsistema_sm_sigla: string | null;
  subsistema_sh_sigla: string | null;
  localizacao_epe: string | null;
  qtd_usinas: number;
  potencia_total_mw: number;
}

export type ColunaOrdenavelConjunto =
  | 'cod_conjunto'
  | 'ponto_de_conexao'
  | 'label'
  | 'uf_sigla'
  | 'subsistema_sm_sigla'
  | 'subsistema_sh_sigla'
  | 'localizacao_epe'
  | 'qtd_usinas'
  | 'potencia_total_mw';

export interface OrdenacaoConjuntos {
  coluna: ColunaOrdenavelConjunto;
  direcao: DirecaoOrdenacao;
}

export interface FiltrosConjuntos {
  // Busca textual (case-insensitive) em cod_conjunto OU ponto_de_conexao OU label.
  // WINDX-024: o `label` ja contem `cod_conjunto` como prefixo, entao buscas
  // pelo codigo continuam funcionando casando o label.
  busca: string;
  // Multi-select; vazio = sem filtro. UF = sigla UF (ex.: 'BA').
  ufs: string[];
  subsistemasSm: string[];
  subsistemasSh: string[];
  localizacoesEpe: string[];
}

// ============ Modelos disponiveis (rota 5.3) ============

/**
 * Cada modelo na resposta tem a flag `disponivel` — a lista inclui TODOS os
 * modelos visíveis (filtrados por `ativo` no backend); a flag `disponivel`
 * marca visualmente quais podem ser selecionados.
 * `dias_previsao` só vem em modo `simulacoes_antigas`.
 *
 * Shape atualizado em 2026-04-24 para bater com BE WINDX-008.2 (backend#28)
 * implementado contra `dim_windx_painel_options` + EXISTS na `fat_windx_*`.
 */
export interface ModeloDisponivel {
  id: number;
  codigo: string;
  label: string;
  dias_previsao?: number;
  disponivel: boolean;
  // ISO 'YYYY-MM-DD' da rodada efetiva quando `disponivel=true` (BE 2026-05-05);
  // `null` quando o modelo nao tem rodada (o BE serializa o campo nulo).
  // WEOL-SM (#99): so no SM a data vem por modelo e por tipo — a da ultima
  // rodada DAQUELE modelo no tipo pedido (omitido = 'final'). No SH continua a
  // `data_referencia` da resposta (a mesma para todos os modelos com dados): o
  // `tipo_rodada` ali so filtra quais modelos tem rodada nessa data.
  data_rodada?: string | null;
}

export interface ModelosDisponiveisResponse {
  modo: 'estudo_automatico' | 'simulacoes_antigas';
  // `data_referencia` presente em modo estudo_automatico (hoje).
  data_referencia?: string;
  // `data_simulacao` presente em modo simulacoes_antigas (D-14..D-1).
  data_simulacao?: string;
  modelos: ModeloDisponivel[];
}

// ============ Chart data semi-horario (rota 5.4) ============
//
// Contrato `windx-definicao/contratos/sh/chart-data.json` v5.0:
// o backend devolve `option` ECharts pronto + `metadata` minima.
// O FE renderiza com <ReactECharts option={data.option} /> direto, sem
// transformacao. Phase A (WINDX-008.4) cobre multi-modelo + multi-entidade
// em frequencia=sh; phases B/C/D adicionam observado/REN/diaria.

export interface ChartMetadataSH {
  frequencia_temporal: Frequencia;
  subdivisao_espacial: number;
  unidade: Unidade;
  tipo_rodada: TipoRodada;
  // WINDX-030 (contrato v7.0): substitui `com_rmv: boolean`. Backend echo
  // da lista enviada no request.
  opcoes_rmv: OpcaoRmv[];
  data_inicio: string; // YYYY-MM-DD
  data_fim: string; // YYYY-MM-DD
  total_pontos: number;
  total_series: number;
  // WINDX-036 (contrato v7.1, ADR-028): lista de nomes de series cujo
  // `data_extensao_efetiva` veio nao-nulo no option.series[] — i.e., ETL
  // caiu em fallback retroativo D-N para extensao GFS/GEFS/GEFS_Update.
  // Vazio quando todas as rodadas foram sincronas (D+0). Default vazio
  // para compat com responses pre-v7.1.
  series_com_extensao_retroativa?: string[];
}

export interface ChartRequestSH {
  frequencia_temporal: Frequencia;
  subdivisao_espacial: number;
  id_entidades: number[];
  id_modelos: number[];
  data_inicio: string; // YYYY-MM-DD
  data_fim: string; // YYYY-MM-DD
  unidade: Unidade;
  tipo_rodada: TipoRodada;
  // WINDX-030 (contrato v7.0): substitui `com_rmv: boolean`. Min 1, max 2.
  // Ambos selecionados duplicam o numero de series no grafico.
  opcoes_rmv: OpcaoRmv[];
  // Phase B/C/D — backend rejeita se true (Phase A so aceita default false).
  incluir_observado?: boolean;
  fonte_observado?: FonteObservado;
  incluir_fator_ren?: boolean;
  data_simulacao?: string; // YYYY-MM-DD; quando informado vira data_rodada.
}

// `option` e um objeto ECharts opaco montado no backend (title, tooltip,
// legend, xAxis, yAxis, series com itemStyle.color etc). Tipamos como
// Record indexavel para nao acoplar o FE a cada campo do ECharts — o
// componente repassa `option` para `<ReactECharts>` sem inspecionar.
export type EChartsOption = Record<string, unknown>;

export interface ChartResponseSH {
  option: EChartsOption;
  metadata: ChartMetadataSH;
}

// ============ Consulta Estimativa / pré-voo (WINDX-051) ============
//
// Pré-voo barato do gráfico de combinação: dado a seleção, estima séries,
// pontos, payload e veredito SEM rodar a query pesada. Contrato:
// `windx-definicao/contratos/common/consulta-estimativa.json`. A regra mora no
// backend (`windx_query_limits.estimate_query_cost`) — o FE só consome.

export type VereditoEstimativa = 'verde' | 'amarelo' | 'vermelho';

export interface ConsultaEstimativaRequest {
  frequencia_temporal: Frequencia;
  subdivisao_espacial: number;
  id_entidades: number[];
  id_modelos: number[];
  opcoes_rmv: OpcaoRmv[];
  data_inicio: string; // YYYY-MM-DD
  data_fim: string; // YYYY-MM-DD
  // FE envia idsSimulacoesAntigasAtivas.length. n_simulacoes = 1 + este valor.
  n_simulacoes_antigas: number;
  incluir_fator_ren: boolean;
}

export type AcaoSugestao =
  | 'agregar_por_conjunto'
  | 'agregar_por_subsistema'
  | 'remover_simulacoes_antigas'
  | 'usar_uma_rmv';

/**
 * Um modelo e elegivel para selecao quando o BE reportou dados para ele E ele
 * suporta o tipo de rodada escolhido. Definicao unica, consumida pelo painel
 * desktop (`ControlPanel/Models` + `CategoriaModelos`) e pelo painel mobile
 * (`Mobile/ControlPanelSheet/Modelos`) — nao duplicar.
 */
export function modeloNaoSuportaTipoRodada(m: Modelo, tipoRodada?: TipoRodada): boolean {
  return !!tipoRodada && !!m.tipo_simulacao && !m.tipo_simulacao.includes(tipoRodada);
}

export function modeloElegivel(
  m: Modelo,
  idsIndisponiveis: Set<number>,
  tipoRodada?: TipoRodada,
): boolean {
  return !idsIndisponiveis.has(m.id) && !modeloNaoSuportaTipoRodada(m, tipoRodada);
}

/**
 * O painel-options da aba traz a etapa Tipo de Rodada: sempre no SH; no
 * Semanal/Comparativo (`tela` 'sm') so com a flag WINDX_SM_TIPOS_RODADA_ENABLED
 * ligada no BE (WEOL-SM #99). Vale so o painel DA aba pedida: na troca de aba o
 * RTK Query devolve em `data` o painel da aba anterior ate o novo chegar, e o BE
 * ecoa a `tela` pedida.
 *
 * Definicao unica do `exibeRunType` (`useControlPanelRules`), consumida tambem
 * pelo restore das Preferencias Salvas e pelo checklist do mobile — nao duplicar.
 */
export function painelExibeTipoRodada(
  opts: PainelOptions | undefined,
  tela: Extract<WindxTela, 'sh' | 'sm'>,
): boolean {
  return opts?.tela === tela && !!opts.tipos_rodada && opts.tipos_rodada.length > 0;
}

export interface EstimativaSugestao {
  acao: AcaoSugestao;
  n_series_resultante: number;
  veredito_resultante: VereditoEstimativa;
}

export interface EstimativaFatores {
  n_entidades: number;
  n_modelos: number;
  n_rmv: number;
  n_simulacoes: number;
  ren_por_usina: boolean;
  subdivisao_espacial: number;
}

export interface EstimativaLimites {
  legibilidade_max_series: number;
  tecnico_max_series: number;
  max_payload_mb: number;
}

export interface EstimativaCusto {
  n_series: number;
  n_series_por_simulacao: number;
  n_pontos_por_serie: number;
  n_pontos_total: number;
  payload_estimado_mb: number;
  veredito: VereditoEstimativa;
  gargalo: 'payload' | 'tecnico_series' | 'legibilidade' | null;
  fator_dominante: 'simulacoes' | 'entidades' | 'modelos' | 'rmv';
  fatores: EstimativaFatores;
  limites: EstimativaLimites;
  sugestoes: EstimativaSugestao[];
}

// ============ Chart data semanal (rota 5.5 — contrato v2.0) ============
//
// Contrato `windx-definicao/contratos/sm/chart-data.json` v2.0:
// o backend (WINDX-009) devolve `option` ECharts pronto + `metadata` minima.
// Frontend renderiza com <ReactECharts option={data.option} /> direto, sem
// transformacao. Phase A cobre multi-modelo + multi-entidade em
// patamar='media'; phases B (L/M/P) e C (REN) ficam para MRs futuros.

export interface ChartMetadataSM {
  subdivisao_espacial: number;
  unidade: Unidade;
  patamar: Patamar;
  com_rmv: boolean;
  semanas_futuras: number;
  data_rodada: string; // YYYY-MM-DD
  // WEOL-SM (#99): eco do tipo consultado. Opcional: o BE anterior ao #99
  // nao envia (equivale a 'final').
  tipo_rodada?: TipoRodada;
  total_pontos: number;
  total_series: number;
}

export interface ChartRequestSM {
  subdivisao_espacial: number;
  id_entidades: number[];
  id_modelos: number[];
  unidade: Unidade;
  patamar: Patamar; // Phase A: 'media' apenas; backend rejeita L/M/P.
  com_rmv: boolean;
  // 1..10; default 5 no BE. WEOL-SM (#99): o horizonte vai ate 10 semanas
  // (o ONS + Climatologia fecha o mes operativo; o Climatologia, #101, tem o
  // mesmo horizonte); o BE corta pelas N primeiras semanas da rodada.
  semanas_futuras?: number;
  // WEOL-SM (#99): rodada preliminar ou final. Opcional: omitido = 'final' no BE.
  tipo_rodada?: TipoRodada;
  // Phase C/D — backend rejeita se true (Phase A bloqueia).
  incluir_fator_ren?: boolean;
  data_simulacao?: string; // YYYY-MM-DD; D-14..D-1 (Estudo Antigo)
}

export interface ChartResponseSM {
  option: EChartsOption;
  metadata: ChartMetadataSM;
}

// ============ Comparativo SM (rota /sm/comparativo/) ============
//
// Contrato `windx-definicao/contratos/sm/comparativo.json` v1.0.
// Tabela pivot de convergencia das previsoes semanais — backend (WINDX-016)
// devolve estrutura JSON pronta para renderizar como tabela
// (nao ECharts). Frontend (FE-WINDX-007) renderiza via react-bootstrap Table.

export interface ComparativoSMRequest {
  // WEOL-SM (#99): qualquer modelo ativo da tela (o enum 13|14 acabou).
  // Default no BE: 13 (ONS); modelo inativo ou inexistente -> code 400.
  id_modelo?: number;
  // WEOL-SM (#99): rodada preliminar ou final. Opcional: omitido = 'final' no BE.
  tipo_rodada?: TipoRodada;
  patamar?: Patamar; // Phase A: 'media' apenas
  num_previsoes?: number; // 1..30, default 15
  subdivisao_espacial?: number; // 1..4, default 1
  id_entidades?: number[] | null; // null = todas do nivel
  unidade?: Unidade; // WINDX-026: default 'mw' in backend
}

export interface ComparativoSMSemana {
  // 'sem1' .. 'semN' — as semanas da ultima rodada; ate 10 (WEOL-SM #99, no
  // ONS + Climatologia e no Climatologia).
  key: string;
  ano_mes_operativo: string; // ex '202618'
  revisao: number;
  data_inicio: string; // YYYY-MM-DD
  data_fim: string; // YYYY-MM-DD
  label: string; // ex '202618-RV0'
}

export interface ComparativoSMEntidade {
  id: number;
  nome: string;
  label: string;
}

// WINDX-031 (contrato v1.2): classificacao verde/vermelho de cada celula
// em relacao ao baseline da coluna (primeira previsao nao-null, normalmente D-1).
// Backend calcula; frontend apenas renderiza. Ver ADR-026.
export type SinalEscala = 'baseline' | 'neutro' | 'positivo' | 'negativo';

export interface CelulaEscala {
  sinal: SinalEscala;
  intensidade: 0 | 1 | 2 | 3;
  delta_pct: number; // ponto percentual (ex: 1.56 = +1.56%)
}

export interface ComparativoSMPrevisao {
  data_rodada: string; // YYYY-MM-DD
  label: string; // 'D-1', 'D-2', ...
  // chave = id_entidade (string), valor = array com 1 valor por semana (mesma
  // ordem de `semanas`, ate 10) ou null quando aquela rodada nao cobria a semana.
  valores: Record<string, (number | null)[]>;
  // WINDX-031: espelha 'valores' com classificacao de cor por celula
  // (null quando o valor original era null). Campo opcional para backcompat
  // com responses anteriores ao bump v1.2.
  escalas?: Record<string, (CelulaEscala | null)[]>;
}

export interface ComparativoSMData {
  ultima_atualizacao: string; // ISO 8601 com TZ
  modelo: { id: number; codigo: string; label: string };
  // WEOL-SM (#99): eco do tipo consultado. Opcional: o BE anterior ao #99
  // nao envia (equivale a 'final').
  tipo_rodada?: TipoRodada;
  patamar: Patamar;
  patamar_label: string;
  unidade: Unidade; // WINDX-026: MW vs FC
  unidade_label: string; // ex: 'Geracao (MWm)' ou 'Fator de Capacidade (%)'
  semanas: ComparativoSMSemana[];
  entidades: ComparativoSMEntidade[];
  previsoes: ComparativoSMPrevisao[];
}

// ============ Download ZIP (rota 5.8) ============
//
// Substituiu o CSV em 2026-05-14. Backend retorna PAR URL (Pre-Authenticated
// Request, 72h) do bucket OCI windx. Cada ZIP corresponde a 1 tupla
// (tipo, data_rodada, modelo, rmv, tipo_rodada). Para multiplos modelos
// selecionados, o frontend chama a rota N vezes em paralelo.

export interface DownloadZipRequest {
  tipo: Extract<WindxTela, 'sh' | 'sm'>;
  // YYYY-MM-DD
  data_rodada: string;
  // Codigo canonico do modelo = diretorio {MODELO} no bucket (ex: 'WRF',
  // 'ONS_Oficial', 'ETA40_SM').
  modelo: string;
  // SH-only; ignorado em SM (o zip SM e sempre com_rmv).
  rmv?: boolean;
  // SH e SM. WEOL-SM (#99): no SM vira o sufixo `-{tipo}` do zip; omitido =
  // 'final' no BE.
  tipo_rodada?: TipoRodada;
}

export interface DownloadZipResponseData {
  // PAR URL do OCI (https://objectstorage....).
  url: string;
  filename: string;
  file_size_bytes: number;
  // Tipicamente 72.
  expires_in_hours: number;
}

// ============ Preferencias (rotas 5.9-5.11 — FE-WINDX-008) ============
//
// preferencias_json armazena apenas os filtros de selecao (nao o intervalo
// de datas, que e calculado dinamicamente pelo grafico). Os campos batem
// exatamente com o contrato canonico em contratos/common/preferencias.json.

export interface PreferenciasJsonSH {
  frequencia_temporal: Frequencia;
  subdivisao_espacial: number;
  id_entidades: number[];
  id_modelos: number[];
  unidade: Unidade;
  tipo_rodada: TipoRodada;
  // WINDX-030 (contrato v7.0): substitui `com_rmv: boolean`.
  opcoes_rmv: OpcaoRmv[];
  // Opcional: preferencias salvas antes desse campo existir nao tem o
  // valor; nesse caso o restore preserva o estado atual.
  ids_simulacoes_antigas_ativas?: number[];
  // Opt-in do overlay "Fator da REN" (2026-05-29). Opcional: prefs salvas
  // antes do campo nao o tem — restore preserva o default (false).
  incluir_fator_ren?: boolean;
}

export interface PreferenciasJsonSM {
  subdivisao_espacial: number;
  id_entidades: number[];
  id_modelos: number[];
  unidade: Unidade;
  patamar: Patamar;
  com_rmv: boolean;
  // WEOL-SM (#99): tipo de rodada do Semanal/Comparativo. Opcional:
  // preferencias salvas antes do campo nao o tem — o restore assume 'final'.
  tipo_rodada?: TipoRodada;
  // Opt-in do overlay "Fator da REN" (2026-05-29). Opcional (backcompat).
  incluir_fator_ren?: boolean;
}

// WINDX-046: tipo da preferencia como o BE/contrato espera. O WEOL-SH separa
// preferencias por sub-aba — semi-horario -> 'sh-semi', diario -> 'sh-diario'.
// O valor 'sh' NAO e aceito pelo BE (enum TipoPreferencia) desde o WINDX-046;
// o FE deve derivar 'sh-semi'/'sh-diario' a partir da granularidade ativa.
export type TipoPreferenciaApi = 'sh-semi' | 'sh-diario' | 'sm' | 'comparativo';

export interface WindxPreferencia {
  id: number;
  nome: string;
  // WINDX-046: 'sh-semi'/'sh-diario' (sub-abas do WEOL-SH) + 'sm' + 'comparativo'.
  tipo: TipoPreferenciaApi;
  created_at: string;
  preferencias_json: PreferenciasJsonSH | PreferenciasJsonSM;
}

export interface SalvarPreferenciaRequest {
  nome: string;
  tipo: TipoPreferenciaApi;
  preferencias_json: PreferenciasJsonSH | PreferenciasJsonSM;
}

// ============================================================================
// Simulacoes Antigas Salvas (WINDX-043 / FE-WINDX-043, WEOL-SH only)
// ----------------------------------------------------------------------------
// CRUD de simulacoes nomeadas com OFFSET RELATIVO em vez de data absoluta.
// ETL retem 15 dias (D-14..D-0); `dias_atras` (1..14) eh re-resolvido a cada
// chamada ao /chart/data/ via `data_simulacao = hoje - dias_atras`, garantindo
// que a simulacao salva nunca aponte para uma rodada apagada.
// Contrato: contratos/sh/simulacoes-antigas.json v1.0 (windx-definicao).
// ADR: docs/ADR/ADR-030_tabela_simulacoes_antigas_salvas.md.

export interface WindxSimulacaoAntiga {
  id: number;
  // 1..30 chars (CHECK no BD + Pydantic + counter no FE)
  nome: string;
  // 1..14 (offset relativo, NUNCA data absoluta)
  dias_atras: number;
  tipo_rodada: TipoRodada;
  id_modelos: number[];
  opcoes_rmv: OpcaoRmv[];
  created_at: string;
  updated_at: string;
}

export interface SalvarSimulacaoAntigaRequest {
  // Sem `id`: cria. Com `id`: o upsert por (user_id, nome) garante UPDATE
  // quando o nome ja existir; o campo `id` em si nao eh enviado ao BE — o
  // FE so o usa internamente para prefill em modo edit.
  nome: string;
  dias_atras: number;
  tipo_rodada: TipoRodada;
  id_modelos: number[];
  opcoes_rmv: OpcaoRmv[];
}
